import { Component } from '@angular/core';
import { DataLayerBaseComponent } from './data-layer-base.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';
import * as d3 from 'd3';
import { withLatestFrom } from 'rxjs/operators';

declare var L;


/*
 * data-layer-geojson-point defines a class to display geojson point data.  While this could be used at the very least
 *	to display point data, there isn't much more functionality offered than that.  Most likely, this is a class from
 *	which other classes will be derived.
 *
 * Example of the data file:
 * --------------------------------
 * {
 *	"type": "FeatureCollection",
 *	"features": [
 *	  {
 *		"type": "Feature",
 *		"properties": {
 *			"data_source": "WETMap"
 *		},
 *		"geometry": {
 *		  "type": "Point",
 *		  "coordinates": [-105.07477, 40.603510]
 *		}
 *	  },
 *	  ...
 *	]
 * }
 * --------------------------------
 *
 * Within the configuration file, point stations can be included:
 * "overlayLayers": [
 * 		{"id": "pointLayer", "label": "Points", "url": "map/overlay-layers/cameraStations.geojson",
 *		 "class": "data-layer-geojson-point", "refresh": false },
 * ...
 * ]
 *
 * 	Cluster icon resources
 *		https://github.com/Leaflet/Leaflet.markercluster/blob/master/example/geojson.html 
 *		http://bl.ocks.org/gisminister/10001728  - really great pie ring around the marker example 
	styleUrls: ['app.component.css', 'data-layer-cluster.css']
 */

@Component({
	selector: 'data-layer-geojson-point',
	template: `
		<div class="checkboxDiv"><label class="overlay-layer"><input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
		<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerGeojsonPointComponent extends DataLayerBaseComponent {

	rmax = 30;		// maximum radius for cluster pies
	categoryField = 'ns';	// fieldname for marker category (used in the pie)
	iconField = 'ns';		// fieldname for marker icon


	constructor(
		mapService: MapService, dataLayerService: DataLayerService, configService: ConfigService, userMessageService: UserMessageService ) {
		super(mapService, dataLayerService, configService, userMessageService);
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
			this.activateLayer();
		} else {
			this.deactivateLayer();
		}

	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}

	setData ( someData: any ) {
		super.setData(someData);
	}

	// first check whether the information has been read.
	// display the markers on the map and set the layer to active
	activateLayer () {
		console.log('Activating ' + this.id + ' layer.');
		if ( this.value == null || this.refresh === true ) {
			// once the data file has been read, drawMarkers is automatically called because an observer has been established
			this.parseDataFile();
		} else {
			this.drawMarkers();
		}

		// activate legend as well
		const legendDisplay = this.mapService.getDataLayerLegendDisplay(this.id);
		if ( legendDisplay != null ) {
			legendDisplay['source'].next (true);
		}

		this.active = true;
	}

	drawMarkers () {
		console.log('Drawing ' + this.value.features.length + ' stations.');
		if ( this.mapLayer != null ) {
				this.mapService.map.removeLayer(this.mapLayer);
		}
		let geojsonLayer = L.geoJson(this.value, {
			// placeMarkers using configuration which specifies which property should be used as the title, popup, etc.
			pointToLayer: this.placeMarkers,
			onEachFeature: this.onEachPointFeature,
			contents: this
		});

		if ( this.clusterMarkers == true ) {

			// https://stackoverflow.com/questions/9644044/javascript-this-pointer-within-nested-function
			// https://stackoverflow.com/questions/38245450/angular2-components-this-is-undefined-when-executing-callback-function
			// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_objects/Function/bind
			// 
			// bind the this pointer to defineClusterIcon so "this" has the proper context
			const defineClusterIconWithThis = this.defineClusterIcon.bind(this);
			this.mapLayer = L.markerClusterGroup({
				// override the default
				maxClusterRadius: 2*this.rmax,
				iconCreateFunction: defineClusterIconWithThis
			});
			this.mapLayer.addLayer(geojsonLayer);
		} else {
			this.mapLayer = geojsonLayer;
		}
		this.mapService.map.addLayer(this.mapLayer);
		/* */


		// This should be very temporary.
		let numLayers = 0;
		this.mapService.map.eachLayer(function(layer) {
			numLayers = numLayers + 1;
		});
		console.log('Map has number of layers: ' + numLayers);
	}

	// extended classes should implement this function
	onEachPointFeature(feature, layer) {
		const stationName = feature.properties['name'] != null ? feature.properties['name'] : '';
		const stationDescription = feature.properties['description'] != null ? feature.properties['description'] : '';
		const linkStr = 'link';
		const imageStr = 'image';
		const height = 'image_height';
		const width = 'image_width';
		let popupContent = '';
		let tooltipContent = '';
		let options = {};

		// Assign HTML content to use in a popup when the user clicks on a marker

		if ( this['contents'].popupTitle != null ) {
			// Assign HTML content to use in a popup when the user clicks on a polygon
			// Multiple properties may be displayed.  They are specified with a comma-separated string.
			const props = this['contents'].popupTitle.split(',');
			popupContent = '<span class=\'tooltipHeader\'>' + feature.properties[props[0]]  + '</span><br>';
			for ( let i = 1; i < props.length; i++ ) {
				popupContent += props[i] +  ': ' + feature.properties[props[i]]  + '</br>';
			}
			tooltipContent = popupContent;
		} else {

			popupContent = '<p style=\'font-size:20px\'>' + stationName +
				' - ' + stationDescription +
				'<img src=\'' + feature.properties[imageStr] + '\' style=\'height:' +
				feature.properties[height] + 'px; width:' + feature.properties[width] + '\'>' +
				'<br><a href=\'' + feature.properties[linkStr] + '\' target=\'_blank\'>' + feature.properties[linkStr] + '</a>';
			options = { 'minWidth': feature.properties[width] };
			tooltipContent = stationName + ' (click for more info)';
		}

		if (feature.properties && feature.properties.popupContent) {
			// This will append content from a "popupContent" property if it is in the GeoJSON file
			popupContent += feature.properties.popupContent;
		}

		layer.bindPopup(popupContent, options);
		layer.bindTooltip(tooltipContent);
	}

	// extended classes should implement this function
	placeMarkers(feature, latlng) {
		let marker;
		const ts = feature.properties.titleString != null ? feature.properties[feature.properties.titleString] : null;
		const title = ts != null ? ts : feature.properties.id != null ? feature.properties.id : feature.properties.name;
		const popupTextColor = 'white';
		/*
		const popupTextColor = this['content'].hasOwnProperty('popupTextColor') ? 
			this['content']['popupTextColor'] : 'white';
			*/
		const markerColor = feature.properties.hasOwnProperty('markerColor') ?
			feature.properties.markerColor : 'blue';
		const iconUrl = feature.properties?.iconUrl;
			
		// if iconUrl specified, use regular icon, otherwise use extra markers icon
		marker = iconUrl != null ?
			L.icon ({
				iconUrl: iconUrl,
				popupAnchor: [0, -40],
				iconSize: [24, 24]
			}) :
			L.ExtraMarkers.icon({
				icon: 'fa-number',
				iconColor: popupTextColor,		// text color
				markerColor: markerColor,	// marker background color
				shape: 'circle',
				prefix: 'fa',
				number: title
			});

		marker.options.shadowSize = [50, 75];

		return L.marker(latlng, {icon: marker});
	}

	deactivateLayer() {
		console.log('Deactivating ' + this.id + ' layer.');
		if ( this.active === true ) {
			this.mapService.map.removeLayer(this.mapLayer);
			this.active = false;
		}
		// deactivate the legend as well
		const legendDisplay = this.mapService.getDataLayerLegendDisplay(this.id);
		if ( legendDisplay != null ) {
			legendDisplay['source'].next (false);
		}

	}

	parseDataFile () {
		console.log('In data-layer-geojson-point.parseDataFile.');
		const self = this;
		console.log(self);

		const requestStations = new XMLHttpRequest();
		requestStations.onload = function ( ) {
			if ( requestStations.readyState === requestStations.DONE && requestStations.status === 200 ) {

				// first, get json file content
				const allStations = JSON.parse(requestStations.responseText);
				// console.log(allStations);
				// assign titleString
				for ( let i = 0; i < allStations.features.length; i++ ) {
					if ( self.titleString != null ) {
						allStations.features[i].properties.titleString = self.titleString;
					}
					if ( self.markerColor != null ) {
						allStations.features[i].properties.markerColor = self.markerColor;
					}
					if ( self.iconUrl != null ) {
						allStations.features[i].properties.iconUrl = self.iconUrl;
					}
				}


				self.value = allStations;
				self.valueObservable.next(allStations);
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
					filename + ' for all ' + self.id + ' stations was successfully completed.');
				// console.log ( dataLayer );

			} else {
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + filename + ' for all ' + self.id + ' stations was UNSUCCESSFUL.');
			}
		};
		const filename = this.url.startsWith('http') ? this.url : this.configService.baseDir + this.url + '?nocache=' + (new Date()).getTime();
		requestStations.open('get', filename, true );
		requestStations.overrideMimeType('text/plain');
		console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + filename + ' for all ' + this.id + ' stations.');
		requestStations.send();
	}

	//
	// functions for cluster icons, see	http://bl.ocks.org/gisminister/10001728  - really great pie ring around the marker example 
	//
	defineClusterIcon (cluster) {
		/*
		{
		console.log (cluster);
		console.log( cluster.getAllChildMarkers());
		// default behavior
		let childCount = cluster.getChildCount();

       	let c = ' marker-cluster-';
       	if (childCount < 10) {
            c += 'small';
      	} else if (childCount < 100) {:
            c += 'medium';
       	} else {
            c += 'large';
       	}

        return new L.DivIcon({ html: '<div><span>' + childCount + '</span></div>', className: 'marker-cluster' + c, iconSize: new L.Point(40, 40) });
	   }
	   */
	   
	   var children = cluster.getAllChildMarkers(),
        n = children.length, //Get number of markers in cluster
        strokeWidth = 1, //Set clusterpie stroke width
        r = this.rmax-2*strokeWidth-(n<10?12:n<100?8:n<1000?4:0), //Calculate clusterpie radius...
        iconDim = (r+strokeWidth)*2, //...and divIcon dimensions (leaflet really want to know the size)
        data = d3.nest() //Build a dataset for the pie chart
          // .key(function(d) { return d.feature.properties[this.categoryField]; })
          .key(function(d) { return d.feature.properties['ns']; })
          .entries(children, d3.map),
		//bake some svg markup
        html = this.bakeThePie({data: data,
                            valueFunc: function(d){return d.values.length;},
                            keyFunc: function(d){return parseInt(d.key);},
                            strokeWidth: 1,
                            outerRadius: r,
                            innerRadius: r-10,
                            pieClass: 'cluster-pie',
                            pieLabel: n,
                            pieLabelClass: 'marker-cluster-pie-label',
                            pathClassFunc: function(d){return "category-"+d.data.key;},
							// pathTitleFunc: function(d){return metadata.fields[this.categoryField].lookup[d.data.key]+' ('+d.data.values.length+' accident'+(d.data.values.length!=1?'s':'')+')';}
							// see code ... metadata = geojson.properties
                          }),
        //Create a new divIcon and assign the svg markup to the html property
        myIcon = new L.DivIcon({
            html: html,
            className: 'marker-cluster', 
            iconSize: new L.Point(iconDim, iconDim)
		});
		// console.log(data[0]);
		// console.log(html);
    return myIcon;
}

/*function that generates a svg markup for the pie chart*/
bakeThePie(options) {
    /*data and valueFunc are required*/
    if (!options.data || !options.valueFunc) {
        return '';
    }
    var data = options.data,
		valueFunc = options.valueFunc,
		keyFunc = options.keyFunc,
        r = options.outerRadius?options.outerRadius:28, //Default outer radius = 28px
        rInner = options.innerRadius?options.innerRadius:r-10, //Default inner radius = r-10
        strokeWidth = options.strokeWidth?options.strokeWidth:1, //Default stroke is 1
        pathClassFunc = options.pathClassFunc?options.pathClassFunc:function(){return '';}, //Class for each path
        pathTitleFunc = options.pathTitleFunc?options.pathTitleFunc:function(){return '';}, //Title for each path
        pieClass = options.pieClass?options.pieClass:'marker-cluster-pie', //Class for the whole pie
        pieLabel = options.pieLabel?options.pieLabel:d3.sum(data,valueFunc), //Label for the whole pie
        maxKey = options.maxKey?options.maxKey:d3.max(data,keyFunc), //max key in the cluster
        pieLabelClass = options.pieLabelClass?options.pieLabelClass:'marker-cluster-pie-label',//Class for the pie label
        
        origo = (r+strokeWidth), //Center coordinate
        w = origo*2, //width and height of the svg element
        h = w,
        donut = d3.pie(),
		arc = d3.arc().innerRadius(rInner).outerRadius(r),
		circle = d3.arc().innerRadius(0).outerRadius(rInner).startAngle(0).endAngle(Math.PI*2);
		
	// So far, so good.  maxKey is the max novascore for this cluster.
	// console.log(maxKey);
	// console.log(data);
    //Create an svg element
    var svg = document.createElementNS(d3.namespaces.svg, 'svg');
    //Create the pie chart
    var vis = d3.select(svg)
        .data([data])
        .attr('class', pieClass)
        .attr('width', w)
        .attr('height', h);
        
    var arcs = vis.selectAll('g.arc')
        .data(donut.value(valueFunc))
        .enter().append('svg:g')
        .attr('class', 'arc')
        .attr('transform', 'translate(' + origo + ',' + origo + ')');
	
    arcs.append('svg:path')
        .attr('class', pathClassFunc)
        .attr('stroke-width', strokeWidth)
        .attr('d', arc)
        .append('svg:title')
		  .text(pathTitleFunc);
		  
	var center = vis.selectAll('g.arc')
        .data(circle)
        .enter().append('svg:g')
        .attr('class', 'category-null') // just changes the inner circle to white unless there is a true dominant novascore color
        .attr('transform', 'translate(' + origo + ',' + origo + ')');
                
    center.append('svg:path')
        .attr('class', 'category-'+maxKey)
        .attr('stroke-width', strokeWidth)
        .attr('d', circle)
        .append('svg:title')
		  .text(pathTitleFunc);
		  
    vis.append('text')
        .attr('x',origo)
        .attr('y',origo)
        .attr('class', pieLabelClass)
        .attr('text-anchor', 'middle')
        //.attr('dominant-baseline', 'central')
        /*IE doesn't seem to support dominant-baseline, but setting dy to .3em does the trick*/
        .attr('dy','.3em')
        .text(pieLabel);
    //Return the svg-markup rather than the actual element
    return this.serializeXmlNode(svg);
}
/*Helper function*/
serializeXmlNode(xmlNode) {
    if (typeof window.XMLSerializer != "undefined") {
        return (new window.XMLSerializer()).serializeToString(xmlNode);
    } else if (typeof xmlNode.xml != "undefined") {
        return xmlNode.xml;
    }
    return "";
}

}
